import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedLayout } from "./components/ProtectedLayout";
import { Layout } from "./components/Layout";

// Import page components
import { LoginPage } from "./pages/LoginPage";
import { WorkspacesPage } from "./pages/WorkspacesPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { TasksPage } from "./pages/TasksPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { FilesPage } from "./pages/FilesPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { NotFoundPage } from "./pages/NotFoundPage";

// Test pages (can be removed in production)
import { WorkspaceDetailPage } from "./pages/WorkspaceDetailPage";

// Venture pages
import { VenturesPage } from "./pages/VenturesPage";
import { VentureDetailPage } from "./pages/VentureDetailPage";
import { CreateVenturePage } from "./pages/CreateVenturePage";
import { EditVenturePage } from "./pages/EditVenturePage";
import { MyPledgesPage } from "./pages/MyPledgesPage";

// Legal pages
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";

/**
 * React Router route configuration
 *
 * Route hierarchy:
 * - / (redirects to /workspaces)
 * - /login (public)
 * - /workspaces (protected)
 *   - /:workspaceId
 *     - /projects
 *       - /:projectId (dashboard integrated here)
 *         - /tasks
 *           - /:taskId
 *             - /files
 *     - /ventures
 *         - /new
 *         - /:ventureId
 *           - /edit
 * - /pledges (user-scoped)
 * - Workspace-level routes:
 *     - /projects (all workspace projects)
 *     - /tasks (all workspace tasks)
 *     - /files (all workspace files)
 *     - /notifications
 * - Legal pages:
 *     - /privacy
 *     - /terms
 * - * (404 Not Found)
 */
export const routes = (
  <Routes>
    {/* Public routes */}
    <Route path="/login" element={<LoginPage />} />

    {/* Protected routes wrapped with ProtectedLayout */}
    <Route element={<ProtectedLayout />}>
      {/* Root redirect to workspaces */}
      <Route path="/" element={<Navigate to="/workspaces" replace />} />

      {/* Dashboard redirect to workspaces (legacy route) */}
      <Route path="/dashboard" element={<Navigate to="/workspaces" replace />} />

      {/* Workspaces routes */}
      <Route element={<Layout />}>
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/workspaces/:workspaceId" element={<WorkspaceDetailPage />} />

        {/* Workspace-level routes */}
        <Route path="/workspaces/:workspaceId/projects" element={<ProjectsPage />} />
        <Route path="/workspaces/:workspaceId/tasks" element={<TasksPage />} />
        <Route path="/workspaces/:workspaceId/projects/:projectId/tasks/:taskId" element={<TaskDetailPage />} />
        <Route path="/workspaces/:workspaceId/files" element={<FilesPage />} />
        <Route path="/workspaces/:workspaceId/notifications" element={<NotificationsPage />} />

        {/* Venture routes */}
        <Route path="/workspaces/:workspaceId/ventures" element={<VenturesPage />} />
        <Route path="/workspaces/:workspaceId/ventures/new" element={<CreateVenturePage />} />
        <Route path="/workspaces/:workspaceId/ventures/:ventureId" element={<VentureDetailPage />} />
        <Route path="/workspaces/:workspaceId/ventures/:ventureId/edit" element={<EditVenturePage />} />

        {/* User-scoped routes */}
        <Route path="/pledges" element={<MyPledgesPage />} />

        {/* Project-level routes */}
        <Route path="/workspaces/:workspaceId/projects/:projectId" element={<ProjectsPage />} />
        <Route path="/workspaces/:workspaceId/projects/:projectId/tasks" element={<TasksPage />} />

        {/* Task-level routes */}
        <Route path="/workspaces/:workspaceId/projects/:projectId/tasks/:taskId" element={<TasksPage />} />
        <Route path="/workspaces/:workspaceId/projects/:projectId/tasks/:taskId/files" element={<FilesPage />} />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Route>
    </Route>

    {/* 404 catch-all route */}
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);
